import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { commonMessages } from 'src/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import { useListServicePlanItems } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { useParams } from 'react-router-dom';
import { booleanBadgeOptions } from 'src/utils/constants';
import CreateServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/CreateServicePlanItemFormModal';
import { useBillableMetricMap } from 'src/features/billableMetrics/hooks/useBillableMetricMap';
import CellServicePlanItemActions from 'src/features/servicePlanItems/components/tables/CellServicePlanItemActions';
import useFilters from 'src/registry/hooks/useFilters';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

type ServicePlanItemsTableProps = {
    isServicePlanActive?: boolean;
};

const ServicePlanItemsTable = ({ isServicePlanActive }: ServicePlanItemsTableProps) => {
    const { formatMessage } = useIntl();
    const { servicePlanId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const metricMap = useBillableMetricMap();

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const {
        data: servicePlanItems,
        isLoading,
        isError,
    } = useListServicePlanItems({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...(servicePlanId && { service_plan: [servicePlanId] }),
    });

    const columnHelper = createColumnHelper<ServicePlanItem>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/service-plan-item/${ctx.row.original.id}`}
                    render={(name, row) => (
                        <div>
                            <div className="text-sm font-medium">{name}</div>
                            <div className="text-sm text-gray-600">{row.code}</div>
                        </div>
                    )}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('billable_metric', {
            header: DataTableColumnHeader,
            enableHiding: true,
            // `billable_metric` is a bare UUID on the wire - the name has to come from the lookup.
            cell: ({ row }) => {
                const metric = metricMap.get(row.original.billable_metric ?? '');
                if (!metric) return '-';
                return (
                    <div>
                        <div className="text-sm font-medium">{metric.name}</div>
                        <div className="text-sm text-gray-600">{metric.code}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(servicePlanItemsMessages.metric),
            },
        }),
        columnHelper.accessor('starting_quantity', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(servicePlanItemsMessages.startingQuantity),
            },
        }),
        columnHelper.accessor('external_item_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(servicePlanItemsMessages.externalId),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(servicePlansMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('is_required', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(servicePlanItemsMessages.isRequired),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: (ctx) => <CellServicePlanItemActions {...ctx} isServicePlanActive={isServicePlanActive} />,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<ServicePlanItem, unknown>
            columns={columns}
            data={servicePlanItems}
            semantics={{
                title: formatMessage(servicePlanItemsMessages.servicePlanItems),
                entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
                entryNames: formatMessage(servicePlanItemsMessages.items).toLowerCase(),
                placeholder: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
            }}
            search={search}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateServicePlanItemFormModal }),
            }}
        />
    );
};

export default ServicePlanItemsTable;
